import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  IoIosArrowBack,
  IoIosArrowForward,
  IoIosArrowDown,
  IoIosArrowUp,
} from "react-icons/io";
import { encodeId } from "../../Helper/encdec";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  } catch {
    return dateStr;
  }
};

const InsightNavigator = ({ currentCallId, disabled = false }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const filteredData = useSelector(
    (state) => state.operationHome.table.filteredData
  );
  const navigatorContext = useSelector(
    (state) => state.operationHome.table.navigatorContext
  );
  // Use popup context list when available, otherwise fall back to main filtered data
  const filteredList = navigatorContext?.length > 0 ? navigatorContext : filteredData;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!filteredList || filteredList.length <= 1) return null;

  const currentIndex = filteredList.findIndex(
    (item) => item.callId === currentCallId
  );
  const displayIndex = currentIndex < 0 ? 0 : currentIndex;
  const currentItem = filteredList[displayIndex];

  const navigateTo = (index) => {
    // Blocked while a PDF download is in flight (see DetailsScreen.js) — the
    // in-flight download reads the currently-displayed call live, so
    // navigating away mid-generation would silently redirect it to the
    // wrong call's content.
    if (disabled) return;
    if (index < 0 || index >= filteredList.length) return;
    const item = filteredList[index];
    const encodedMemberId = encodeId(
      String(item.memberId).replace(/\s+/g, "")
    );
    const encodedCallId = encodeId(item.callId);
    setIsOpen(false);
    navigate(`/detailsPage/${encodedMemberId}/${encodedCallId}`);
  };

  const isFirst = displayIndex <= 0;
  const isLast = displayIndex >= filteredList.length - 1;

  return (
    <div className="insight-navigator" ref={dropdownRef}>

      {/* Left Arrow */}
      <button
        className="nav-arrow-btn"
        onClick={() => navigateTo(displayIndex - 1)}
        disabled={isFirst || disabled}
        title={disabled ? "Navigation is disabled while a PDF is generating" : "Previous record"}
      >
        <IoIosArrowBack />
      </button>

      {/* Trigger — labeled field card */}
      <div
        className={`nav-trigger${isOpen ? " nav-trigger--open" : ""}${disabled ? " icon-disabled" : ""}`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        title={disabled ? "Navigation is disabled while a PDF is generating" : undefined}
      >
        {/* Single row — all 5 fields */}
        <div className="nav-trigger-body">
          <div className="nav-trigger-field nav-trigger-field--id">
            <span className="nav-field-label">MH ID</span>
            <span className="nav-field-value nav-field-value--id">
              {currentItem?.memberId || "—"}
            </span>
          </div>
          <div className="nav-trigger-field nav-trigger-field--name">
            <span className="nav-field-label">Member Name</span>
            <span className="nav-field-value">
              {currentItem?.memberName || "—"}
            </span>
          </div>
          <div className="nav-trigger-field nav-trigger-field--cat">
            <span className="nav-field-label">Call Category</span>
            <span className="nav-field-value">
              {currentItem?.callCategory || "—"}
            </span>
          </div>
          <div className="nav-trigger-field nav-trigger-field--agent">
            <span className="nav-field-label">Agent Name</span>
            <span className="nav-field-value">
              {currentItem?.agentName || "—"}
            </span>
          </div>
          <div className="nav-trigger-field nav-trigger-field--date">
            <span className="nav-field-label">Engagement Date</span>
            <span className="nav-field-value">
              {formatDate(currentItem?.engagementDate1)}
            </span>
          </div>
        </div>

        {/* Right: counter + chevron */}
        <div className="nav-trigger-meta">
          <span className="nav-trigger-count">
            {displayIndex + 1}&nbsp;/&nbsp;{filteredList.length}
          </span>
          <span className="nav-trigger-chevron">
            {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </span>
        </div>
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="nav-panel">

          {/* Column headers */}
          <div className="nav-panel-header">
            <span>MH ID</span>
            <span>Member Name</span>
            <span>Call Category</span>
            <span>Agent Name</span>
            <span>Engagement Date</span>
          </div>

          <div className="nav-panel-body">
            {filteredList.map((item, idx) => (
              <div
                key={item.callId || idx}
                className={`nav-panel-row${
                  idx === displayIndex ? " nav-panel-row--active" : ""
                }`}
                onClick={() => navigateTo(idx)}
              >
                <span className="nav-cell nav-cell--id">
                  {item.memberId || "—"}
                </span>
                <span className="nav-cell">{item.memberName || "—"}</span>
                <span className="nav-cell">{item.callCategory || "—"}</span>
                <span className="nav-cell">{item.agentName || "—"}</span>
                <span className="nav-cell">
                  {formatDate(item.engagementDate1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Right Arrow */}
      <button
        className="nav-arrow-btn"
        onClick={() => navigateTo(displayIndex + 1)}
        disabled={isLast || disabled}
        title={disabled ? "Navigation is disabled while a PDF is generating" : "Next record"}
      >
        <IoIosArrowForward />
      </button>

    </div>
  );
};

export default InsightNavigator;
